import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase/client';
import { hasStreetSalesScope } from '@/lib/business/streetSalesScope';

const db = supabase as any;

export default function SellerShiftGate() {
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasShift, setHasShift] = useState(false);
  const [operatorName, setOperatorName] = useState('');
  const [openingAmount, setOpeningAmount] = useState('0');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const requiresB2BShift = hasStreetSalesScope(user);

  useEffect(() => {
    if (!user?.id) { setOperatorName(''); return; }
    const cachedName = localStorage.getItem(`fooddash:cash-operator:user:${user.id}`) ?? '';
    setOperatorName(cachedName);
    if (cachedName) return;
    void supabase.auth.getUser().then(({ data }) => {
      const savedName = data.user?.user_metadata?.cash_operator_name;
      if (typeof savedName === 'string' && savedName.trim()) setOperatorName(savedName.trim());
    });
  }, [user?.id]);

  const load = useCallback(async () => {
    if (user?.role !== 'employee' || (user.isB2B && !requiresB2BShift) || !user.businessId) { setHasShift(true); setChecking(false); return; }
    const { data } = await db.from('cash_sessions').select('id').eq('business_id', user.businessId).eq('user_id', user.id).is('closed_at', null).maybeSingle();
    setHasShift(Boolean(data));
    setChecking(false);
  }, [requiresB2BShift, user?.businessId, user?.id, user?.isB2B, user?.role]);

  useEffect(() => {
    void load();
    const sessionListener = () => void load();
    const requireShiftListener = () => { setDismissed(false); void load(); };
    window.addEventListener('fooddash:cash-session-changed', sessionListener);
    window.addEventListener('fooddash:require-cash-session', requireShiftListener);
    return () => {
      window.removeEventListener('fooddash:cash-session-changed', sessionListener);
      window.removeEventListener('fooddash:require-cash-session', requireShiftListener);
    };
  }, [load]);

  useEffect(() => {
    if (!dismissed || hasShift) return;
    const reminder = window.setTimeout(() => setDismissed(false), 5 * 60 * 1000);
    return () => window.clearTimeout(reminder);
  }, [dismissed, hasShift]);

  if (checking || hasShift || (dismissed && !requiresB2BShift) || user?.role !== 'employee' || (user.isB2B && !requiresB2BShift) || !user.businessId) return null;
  const openShift = async () => {
    if (operatorName.trim().length < 2) { setError('Escribe el nombre de la persona que operará esta caja.'); return; }
    setBusy(true); setError('');
    const { data, error: insertError } = await db.from('cash_sessions').insert({ business_id: user.businessId, user_id: user.id, opening_amount: Math.max(0, Number(openingAmount) || 0) }).select('id').single();
    setBusy(false);
    if (insertError) { setError(insertError.message); return; }
    localStorage.setItem(`fooddash:cash-operator:${data.id}`, operatorName.trim());
    localStorage.setItem(`fooddash:cash-operator:user:${user.id}`, operatorName.trim());
    void supabase.auth.updateUser({ data: { cash_operator_name: operatorName.trim() } });
    setHasShift(true);
    window.dispatchEvent(new Event('fooddash:cash-session-changed'));
  };

  return <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/65 p-4 backdrop-blur-sm">
    <section className="relative w-full max-w-md rounded-3xl bg-background-50 p-6 shadow-2xl">
      {!requiresB2BShift && <button type="button" onClick={() => setDismissed(true)} aria-label="Cerrar aviso de apertura de turno" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900">
        <i className="ri-close-line text-2xl"/>
      </button>}
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-primary-100 text-primary-600"><i className="ri-cash-line text-3xl"/></div>
      <h2 className="mt-4 text-center text-2xl font-black">Abre tu turno para vender</h2>
      <p className="mt-2 text-center text-sm text-foreground-500">Registra quién atenderá y el fondo inicial de esta caja.</p>
      <div className="mt-6 space-y-4">
        <label className="block text-sm font-bold">Nombre del vendedor<input autoFocus value={operatorName} onChange={(event) => setOperatorName(event.target.value)} className="mt-1 h-12 w-full rounded-xl border border-background-300 bg-background-50 px-4"/></label>
        <label className="block text-sm font-bold">Fondo inicial<input type="number" min="0" inputMode="decimal" value={openingAmount} onChange={(event) => setOpeningAmount(event.target.value)} className="mt-1 h-12 w-full rounded-xl border border-background-300 bg-background-50 px-4 text-lg font-bold"/></label>
        {error && <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        <button disabled={busy || operatorName.trim().length < 2} onClick={() => void openShift()} className="h-13 w-full rounded-xl bg-primary-600 py-3 font-extrabold text-white disabled:opacity-50">{busy ? 'Abriendo turno…' : 'Abrir caja y comenzar a vender'}</button>
      </div>
    </section>
  </div>;
}
